import React, { useState } from 'react';
import axios from 'axios';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './HillviewVolunteerForm.css';

const TIME_SLOTS = [
  { key: 'slot_430', label: '4:30 PM - 5:30 PM' },
  { key: 'slot_530', label: '5:30 PM - 6:30 PM' },
  { key: 'slot_630', label: '6:30 PM - 7:30 PM' },
];

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  emerg_name: string;
  emerg_relationship: string;
  emerg_phone: string;
  volunteer_type: 'youth' | 'parent' | '';
  time_slots: string[];
  dob: string;
  guardian_name: string;
  guardian_phone: string;
  guardian_email: string;
  consent: boolean;
}

interface SuccessState {
  document_id: string;
  view_url: string;
  signers_notified: string[];
  message: string;
}

const EMPTY: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  emerg_name: '',
  emerg_relationship: '',
  emerg_phone: '',
  volunteer_type: '',
  time_slots: [],
  dob: '',
  guardian_name: '',
  guardian_phone: '',
  guardian_email: '',
  consent: false,
};

const HillviewVolunteerForm: React.FC = () => {
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    setForm(prev => ({
      ...prev,
      [field]: target.type === 'checkbox' ? target.checked : target.value,
    }));
  };

  const toggleSlot = (label: string) => {
    setForm(prev => ({
      ...prev,
      time_slots: prev.time_slots.includes(label)
        ? prev.time_slots.filter(s => s !== label)
        : [...prev.time_slots, label],
    }));
  };

  const validate = (): string | null => {
    if (!form.first_name.trim() || !form.last_name.trim()) return 'Full name is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!form.phone.trim()) return 'Phone is required.';
    if (!form.emerg_name.trim()) return 'Emergency contact name is required.';
    if (!form.emerg_relationship.trim()) return 'Emergency contact relationship is required.';
    if (!form.emerg_phone.trim()) return 'Emergency contact phone is required.';
    if (!form.volunteer_type) return 'Please select your volunteer type.';
    if (form.volunteer_type === 'youth') {
      if (!form.dob) return 'Date of birth is required for youth volunteers.';
      if (!form.guardian_name.trim()) return 'Guardian name is required for youth volunteers.';
      if (!form.guardian_phone.trim()) return 'Guardian phone is required for youth volunteers.';
      if (!form.guardian_email.trim()) return 'Guardian email is required for youth volunteers.';
    }
    if (form.time_slots.length === 0) return 'Please select at least one time slot.';
    if (!form.consent) return 'Please read and accept the acknowledgements.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const { data } = await axios.post(
        `${config.BACKEND_URL}/api/event-volunteers/hillview-carnival`,
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          emerg_name: form.emerg_name.trim(),
          emerg_relationship: form.emerg_relationship.trim(),
          emerg_phone: form.emerg_phone.trim(),
          volunteer_type: form.volunteer_type,
          time_slots: form.time_slots,
          ...(form.volunteer_type === 'youth' && {
            dob: form.dob,
            guardian_name: form.guardian_name.trim(),
            guardian_phone: form.guardian_phone.trim(),
            guardian_email: form.guardian_email.trim(),
          }),
          consent: form.consent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(data);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again or contact admin@oarayouthinnovationlab.ca'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="hvf-page">
        <div className="hvf-admin-bar">
          Signed in as {user?.email}
        </div>
        <div className="hvf-card hvf-success-card">
          <div className="hvf-success-icon">&#10003;</div>
          <h1 className="hvf-success-title">Registration received</h1>
          <p className="hvf-success-name">
            <strong>{form.first_name} {form.last_name}</strong> has been registered.
          </p>
          <p className="hvf-success-msg">{success.message}</p>

          {success.signers_notified.length > 0 && (
            <div className="hvf-notified-box">
              <div className="hvf-notified-label">Signing emails sent to:</div>
              {success.signers_notified.map(email => (
                <div key={email} className="hvf-notified-email">{email}</div>
              ))}
            </div>
          )}

          <div className="hvf-next-box">
            <strong>Volunteer: check your email for a signing link from oarasign.</strong>
            <ol>
              <li>Open the email from oarasign</li>
              <li>Click the link to review and sign the form</li>
              <li>The OARA president will countersign after all parties have signed</li>
            </ol>
          </div>

          <button
            className="hvf-btn hvf-btn-primary"
            onClick={() => { setSuccess(null); setForm(EMPTY); }}
          >
            Register next volunteer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hvf-page">
      <div className="hvf-admin-bar">
        Signed in as {user?.email} &nbsp;&mdash;&nbsp; hand device to volunteer to fill this form
      </div>
      <div className="hvf-card">
        <div className="hvf-org-bar">
          <div className="hvf-org-name">OARA Youth Innovation Lab</div>
          <div className="hvf-org-sub">Federally registered not-for-profit</div>
        </div>

        <h1 className="hvf-title">Volunteer Sign-Up</h1>
        <p className="hvf-event-meta">
          Spring Carnival at Hillview Elementary&nbsp;&nbsp;|&nbsp;&nbsp;
          Wednesday May 6, 2026&nbsp;&nbsp;|&nbsp;&nbsp;4:30 PM - 7:30 PM
        </p>

        <form onSubmit={handleSubmit} noValidate>

          <section className="hvf-section">
            <div className="hvf-section-title">Your Information</div>
            <div className="hvf-row">
              <div className="hvf-field">
                <label>First Name <span className="hvf-req">*</span></label>
                <input type="text" value={form.first_name} onChange={set('first_name')}
                  autoComplete="given-name" required />
              </div>
              <div className="hvf-field">
                <label>Last Name <span className="hvf-req">*</span></label>
                <input type="text" value={form.last_name} onChange={set('last_name')}
                  autoComplete="family-name" required />
              </div>
            </div>
            <div className="hvf-row">
              <div className="hvf-field">
                <label>Email <span className="hvf-req">*</span></label>
                <input type="email" value={form.email} onChange={set('email')}
                  autoComplete="email" required />
              </div>
              <div className="hvf-field">
                <label>Phone <span className="hvf-req">*</span></label>
                <input type="tel" value={form.phone} onChange={set('phone')}
                  autoComplete="tel" required />
              </div>
            </div>
          </section>

          <section className="hvf-section">
            <div className="hvf-section-title">Emergency Contact</div>
            <div className="hvf-field">
              <label>Full Name <span className="hvf-req">*</span></label>
              <input type="text" value={form.emerg_name} onChange={set('emerg_name')} required />
            </div>
            <div className="hvf-row">
              <div className="hvf-field">
                <label>Relationship <span className="hvf-req">*</span></label>
                <input type="text" value={form.emerg_relationship}
                  onChange={set('emerg_relationship')}
                  placeholder="e.g. Parent, Spouse" required />
              </div>
              <div className="hvf-field">
                <label>Phone <span className="hvf-req">*</span></label>
                <input type="tel" value={form.emerg_phone} onChange={set('emerg_phone')} required />
              </div>
            </div>
          </section>

          <section className="hvf-section">
            <div className="hvf-section-title">Volunteer Type <span className="hvf-req">*</span></div>
            <div className="hvf-radio-group">
              <label className="hvf-radio-label">
                <input type="radio" name="volunteer_type" value="parent"
                  checked={form.volunteer_type === 'parent'}
                  onChange={set('volunteer_type')} />
                Parent or community member
              </label>
              <label className="hvf-radio-label">
                <input type="radio" name="volunteer_type" value="youth"
                  checked={form.volunteer_type === 'youth'}
                  onChange={set('volunteer_type')} />
                Youth - previously participated in FLL with OARA
              </label>
            </div>

            {form.volunteer_type === 'youth' && (
              <div className="hvf-guardian-section">
                <div className="hvf-guardian-title">
                  Parent or Guardian Consent (required for youth volunteers)
                </div>
                <div className="hvf-field">
                  <label>Youth date of birth <span className="hvf-req">*</span></label>
                  <input type="date" value={form.dob} onChange={set('dob')} required />
                </div>
                <div className="hvf-field">
                  <label>Parent or Guardian full name <span className="hvf-req">*</span></label>
                  <input type="text" value={form.guardian_name}
                    onChange={set('guardian_name')} required />
                </div>
                <div className="hvf-row">
                  <div className="hvf-field">
                    <label>Guardian phone <span className="hvf-req">*</span></label>
                    <input type="tel" value={form.guardian_phone}
                      onChange={set('guardian_phone')} required />
                  </div>
                  <div className="hvf-field">
                    <label>Guardian email <span className="hvf-req">*</span></label>
                    <input type="email" value={form.guardian_email}
                      onChange={set('guardian_email')} required />
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="hvf-section">
            <div className="hvf-section-title">
              Available Time Slots <span className="hvf-req">*</span>
            </div>
            <p className="hvf-hint">Select one or more hours you can help at the OARA booth.</p>
            <div className="hvf-slots">
              {TIME_SLOTS.map(({ key, label }) => (
                <label
                  key={key}
                  className={`hvf-slot-label ${form.time_slots.includes(label) ? 'hvf-slot-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={form.time_slots.includes(label)}
                    onChange={() => toggleSlot(label)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </section>

          <section className="hvf-section">
            <div className="hvf-section-title">Acknowledgements</div>
            <div className="hvf-ack-scroll">
              <p><strong>1. OARA Lead Supervision</strong><br />
                Renjith Madhavan will be present throughout the event. Remain in his line of sight
                at all times when youth participants are present.
              </p>
              <p><strong>2. Two-Adult Rule</strong><br />
                You will not be alone with any youth participant at any time. If you find yourself
                the only adult with a child, move immediately to a visible area and call for another
                adult.
              </p>
              <p><strong>3. Photography</strong><br />
                Do not photograph or video-record youth participants on personal devices.
              </p>
              <p><strong>4. Mandatory Reporting</strong><br />
                If you have reasonable grounds to believe a child has been, or is at risk of being,
                abused or neglected, you have a personal legal duty to report to BC Child Protection
                Services: 1-800-663-9122 (24/7).
              </p>
              <p><strong>5. Conduct</strong><br />
                Maintain professional, respectful conduct with all youth, families, and community
                members. Refer any program questions to the OARA lead.
              </p>
              <p><strong>6. Health and Safety</strong><br />
                Report any injury or unsafe condition to the OARA lead immediately.
                Emergency: 911 | WorkSafeBC: 1-888-967-5377
              </p>
            </div>
            <label className="hvf-consent-row">
              <input type="checkbox" checked={form.consent}
                onChange={e => setForm(prev => ({ ...prev, consent: e.target.checked }))} />
              <span>
                I have read and agree to the acknowledgements above. I will attend the pre-event
                briefing before beginning my volunteer activities. <span className="hvf-req">*</span>
              </span>
            </label>
          </section>

          {error && <div className="hvf-error">{error}</div>}

          <button
            type="submit"
            className="hvf-btn hvf-btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Registration'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default HillviewVolunteerForm;
